import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useEarnDashboardQuery } from 'hooks/queries/Earn/useEarnDashboardQuery'
import { formatAmount, formatAPY } from 'pages/Rewards/utils'
import { EARN_ASSET_STATUS } from 'types/earn'
import { EarnCustomerRewardsProgram } from './components/EarnCustomerRewardsProgram'
import { EarnMyAsset, EarnMyAssets } from './components/EarnMyAssets'
import { EarnSummary } from './components/EarnSummary'
import { EarnTotalEarnings } from './components/EarnTotalEarnings'

const formatUsd = (amount: string | number | null | undefined) =>
  `$${formatAmount(Number(amount ?? 0))}`

export const Earn = () => {
  const { t } = useTranslation()
  const { data, isLoading } = useEarnDashboardQuery()
  const { accountCapabilities } = useAccountsContext()

  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  } = accountCapabilities || {}
  const isEmbeddedPartner =
    isEmbeddedCustomerCompanyCapabilityEnabled || isEmbeddedCustomerIndividualCapabilityEnabled

  const isDirectEarning = !isEmbeddedPartner

  const assets: EarnMyAsset[] = useMemo(
    () =>
      (data?.earnAssets ?? []).map(asset => {
        const comingSoon = asset.status === EARN_ASSET_STATUS.COMING_SOON

        return {
          currency: asset.asset,
          productName: asset.productName,
          comingSoon,
          myEarnRate: asset.myEarnRate != null ? formatAPY(asset.myEarnRate) : undefined,
          customerEarnRate:
            asset.customerEarnRate != null ? formatAPY(asset.customerEarnRate) : undefined,
          availableFundsLabel: asset.earningSince
            ? t('earn.earningSince', { date: dayjs(asset.earningSince).format("MMM ['']YY") })
            : undefined,
          onWithdrawClick: comingSoon ? undefined : () => [],
          onDepositClick: comingSoon ? undefined : () => [],
        }
      }),
    [data?.earnAssets, t]
  )

  const myAssetsTable = <EarnMyAssets assets={assets} showCustomerEarnRate={!isDirectEarning} />

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (isDirectEarning) {
    return (
      <Box flexCol gap={16}>
        <EarnSummary
          allocatedFunds={formatUsd(data?.fundsOverview.allocatedFunds)}
          availableFunds={formatUsd(data?.fundsOverview.availableFunds)}
          totalAllTimeEarnings={formatUsd(data?.totalEarnings.total)}
        />
        {myAssetsTable}
      </Box>
    )
  }

  return (
    <Box flexCol gap={16}>
      <Box flex gap={16} className="items-stretch max-lg:flex-col">
        <Box flexCol gap={16} className="flex-1 min-w-0">
          <EarnSummary
            allocatedFunds={formatUsd(data?.fundsOverview.allocatedFunds)}
            availableFunds={formatUsd(data?.fundsOverview.availableFunds)}
          />
          <EarnTotalEarnings
            allocationEarnings={formatUsd(data?.totalEarnings.fromMyAllocation)}
            customerEarnings={formatUsd(data?.totalEarnings.fromCustomers)}
            totalEarnings={formatUsd(data?.totalEarnings.total)}
          />
        </Box>
        <Box className="flex-1 min-w-0">
          <EarnCustomerRewardsProgram
            onDistributeEarningsClick={() => []}
            totalBalance={formatUsd(data?.customerRewardsProgram.totalBalanceAcrossCustomers)}
            retainedEarningsPercentage={formatAPY(
              data
                ? (Number(data.customerRewardsProgram.remainingTotal) /
                    (Number(data.customerRewardsProgram.distributedEarnings) +
                      Number(data.customerRewardsProgram.remainingTotal) || 1)) *
                    100
                : 0
            )}
          />
        </Box>
      </Box>
      {myAssetsTable}
    </Box>
  )
}
